import type { Composer } from 'vue-i18n';
import type { RouteLocationNormalized, Router } from 'vue-router';
import type { AppLocale } from './index';

type MessageLoader = () => Promise<{ default: Record<string, unknown> }>;

// Fallback for screens that have not declared `meta.i18n` yet: slug = first URL segment,
// which only resolves when the folder name happens to equal it. Nested routes
// (`/setup/store` → `setup`) borrow a sibling's bundle through this path.
// Delete once every screen owning strings declares its own bundle.
const legacyMessages = import.meta.glob<{ default: Record<string, unknown> }>(
  '/src/pages/*/i18n/*.ts',
);

// `fallbackLocale: 'en'` chỉ đỡ được khi cây `en` THẬT SỰ có namespace đó. Bundle global thì
// `boot/i18n.ts` ship tĩnh, còn bundle của MÀN thì không: phiên mới ở `vi` vào màn A chỉ nạp
// `vi/A`, `en/A` chưa từng tồn tại — chunk `vi/A` hỏng là raw key, không gì đỡ. Nạp kèm bản `en`
// (chunk nhỏ, cache lại ngay) để lưới fallback có chỗ thật mà rơi về.
const FALLBACK_LOCALE: AppLocale = 'en';

async function mergeFallback(
  i18n: Composer,
  loaders: Record<string, MessageLoader>,
  locale: AppLocale,
  label: string,
): Promise<void> {
  if (locale === FALLBACK_LOCALE) {
    return;
  }
  const entry = Object.entries(loaders).find(([file]) =>
    file.endsWith(`/${FALLBACK_LOCALE}.ts`),
  );
  if (entry) {
    await mergeMessages(i18n, FALLBACK_LOCALE, entry[1], label);
  }
}

async function mergeMessages(
  i18n: Composer,
  locale: AppLocale,
  load: MessageLoader,
  label: string,
): Promise<void> {
  try {
    const mod = await load();
    i18n.mergeLocaleMessage(locale, mod.default);
  } catch (error) {
    console.error(`[i18n] Failed to load feature messages for "${label}/${locale}"`, error);
  }
}

// Screen i18n co-located at pages/<slug>/i18n/<locale>.ts. Each screen's `route.ts` binds its
// own bundle via `meta.i18n = import.meta.glob('./i18n/*.ts')`, which Vite resolves against
// that file at build time — so renaming the folder moves the bundle with it and no slug
// string can drift out of sync with the URL.
//
// Active locale ONLY — merging all 4 registers empty locales in `availableLocales`,
// making useLang's language-switch skip loading their global messages.
//
// `targetLocale` cho phép nạp bundle của một locale CHƯA active: `setI18nLanguage` phải nạp
// xong rồi mới lật `locale`, nên lúc gọi đây `activeLocale(i18n)` vẫn là locale cũ.
export async function loadFeatureI18n(
  i18n: Composer,
  to: RouteLocationNormalized,
  targetLocale?: AppLocale | string,
): Promise<void> {
  const locale = (targetLocale ?? i18n.locale.value) as AppLocale;

  let declared = false;
  for (const record of to.matched) {
    // Per-record meta, NOT the parent/child-merged `to.meta`: each record owns its bundle.
    const loaders = record.meta.i18n;
    if (!loaders) {
      continue;
    }
    // Match on the locale file rather than the whole key — Vite owns the key's prefix form.
    const entry = Object.entries(loaders).find(([file]) => file.endsWith(`/${locale}.ts`));
    if (!entry) {
      continue;
    }
    declared = true;
    await mergeMessages(i18n, locale, entry[1], record.path);
    await mergeFallback(i18n, loaders, locale, record.path);
  }

  // Only when NO matched record contributed — a declaring screen that fails to load must not
  // silently fall back onto a sibling's bundle.
  if (declared) {
    return;
  }

  const slug = to.path.split('/')[1];
  if (!slug) {
    return;
  }

  const fallback = legacyMessages[`/src/pages/${slug}/i18n/${locale}.ts`];
  if (fallback) {
    await mergeMessages(i18n, locale, fallback, slug);
    const en = legacyMessages[`/src/pages/${slug}/i18n/${FALLBACK_LOCALE}.ts`];
    if (locale !== FALLBACK_LOCALE && en) {
      await mergeMessages(i18n, FALLBACK_LOCALE, en, slug);
    }
  }
}

/**
 * Gắn hai hook router lo bundle co-locate. Tách khỏi `boot/i18n.ts` để GHIM ĐƯỢC —
 * `boot/*.ts` không mount được trong test nên mọi nhánh trong đó vô hình với suite.
 *
 * `beforeEach` đọc locale TRƯỚC khi await, nên nếu người dùng đổi ngôn ngữ GIỮA LÚC điều hướng
 * thì bundle vừa nạp là của locale CŨ — mà `setI18nLanguage` lúc đó nạp cho `currentRoute`, vẫn
 * là route CŨ (vue-router chỉ cập nhật sau khi confirm). Kết quả: màn ĐÍCH không có bundle nào
 * cho locale mới, và `mergeLocaleMessage` không repaint nổi một `t()` đã trượt khoá ⇒ kẹt vĩnh
 * viễn, không phải nháy một khung hình.
 *
 * Nên chụp locale lúc vào, và `afterEach` CHỈ nạp lại khi nó đã đổi — không thì mỗi lần điều
 * hướng chạy deep-merge gấp đôi cho đúng cùng một thứ.
 *
 * ⚠ Lưới này CHỮA CÂY MESSAGE, KHÔNG chữa được khung hình đã lỡ. Đo lại trên vue-i18n 11.4.2
 * (có `window`): `t()` đã TRƯỢT khoá thì merge muộn không repaint; chỉ khoá ĐÃ CÓ rồi bị merge
 * đè mới repaint. Nên sau `afterEach` cây `zh` có đủ khoá mà ô chữ vẫn kẹt raw key nếu nó đã
 * render trước lúc merge. Giá trị thật của lưới: mọi thứ render SAU đó (và mọi lần vào lại màn)
 * đều đúng, thay vì màn đó vĩnh viễn không có bundle nào cho locale mới.
 *
 * ⚠ `failure` KHÔNG được bỏ — xem chú thích cùng vấn đề ở `router/document-title.ts`.
 * Bỏ nó là nạp bundle cho route chưa từng tới VÀ ghi đè `localeAtNavStart` bằng một mốc sai.
 */
export function installI18nRouterHooks(i18n: Composer, router: Router): void {
  let localeAtNavStart: string | null = null;

  router.beforeEach(async (to) => {
    localeAtNavStart = i18n.locale.value;
    await loadFeatureI18n(i18n, to);
  });

  router.afterEach((to, _from, failure) => {
    if (failure) {
      return;
    }
    const now = i18n.locale.value;
    if (localeAtNavStart === now) {
      return;
    }
    localeAtNavStart = now;
    // vue-router KHÔNG await `afterEach`; lỗi nạp đã được `mergeMessages` nuốt nên không có
    // unhandled rejection, `void` chỉ để nói rõ là cố ý thả.
    void loadFeatureI18n(i18n, to);
  });
}
